/**
 * User Authentication Service - Implements proper user authentication flow
 */

import { 
  initiateAuthorizationCodeLogin,
  checkAuthenticationStatus as checkAuthStatus,
  getUserInfoWithToken,
  getCachedUserInfo,
  signOut as authCodeSignOut
} from './auth-code-service';

/**
 * Initiate user login flow
 * This redirects the user to Azure AD for authentication
 */
export const initiateUserLogin = () => {
  try {
    console.log('Initiating user login flow...');
    return initiateAuthorizationCodeLogin();
  } catch (error) {
    console.error('Error initiating user login:', error);
    throw error;
  }
};

/**
 * Check if we have a valid user token from the callback
 * This should be called when the app loads to check for authentication status
 */
export const checkAuthenticationStatus = async () => {
  try {
    return await checkAuthStatus();
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return { isAuthenticated: false, userInfo: null };
  }
};

/**
 * Get user information using a stored token
 * @returns {Promise<object|null>} User information or null
 */
export const getUserInfoWithStoredToken = async () => {
  try {
    return await getUserInfoWithToken();
  } catch (error) {
    console.error('Error getting user info with stored token:', error);
    return null;
  }
};

/**
 * Get user information from backend service
 * @param {string} token - Access token
 * @returns {Promise<object|null>} User information or null
 */
export const getUserInfoFromBackend = async (token) => {
  try {
    return await getUserInfoWithToken();
  } catch (error) {
    console.error('Error getting user info from backend:', error);
    return null;
  }
};



/**
 * Handle authentication callback
 * This should be called on a callback page to process the authentication result
 * @param {string} code - Authorization code from URL params
 * @param {string} state - State parameter from URL params
 * @param {string} error - Error parameter from URL params
 * @returns {Promise<object>} Authentication result
 */
export const handleAuthCallback = async (code, state, error) => {
  // Import the function here to avoid circular dependency
  const { handleAuthorizationCallback } = await import('./auth-code-service');
  
  try {
    return await handleAuthorizationCallback(code, state, error);
  } catch (error) {
    console.error('Error handling authentication callback:', error);
    throw error;
  }
};

/**
 * Sign out the user
 */
export const signOut = () => {
  try {
    return authCodeSignOut();
  } catch (error) {
    console.error('Error during sign out:', error);
  }
};

/**
 * Get current user info (synchronous)
 * @returns {object|null} Current user information
 */
export const getCurrentUserInfo = () => {
  try {
    return getCachedUserInfo();
  } catch (error) {
    console.error('Error getting current user info:', error);
    return null;
  }
};

/**
 * Refresh user information
 * @returns {Promise<object|null>} Updated user information
 */
export const refreshUserInfo = async () => {
  try {
    console.log('Refreshing user information...');
    const userInfo = await getUserInfoWithToken();
    return userInfo;
  } catch (error) {
    console.error('Error refreshing user info:', error);
    return null;
  }
};
